import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldAlert, LogOut } from 'lucide-react';
import { toast } from 'sonner';

import {
  getImpersonationEndsAt,
  getImpersonationTargetEmail,
  isImpersonating,
  stopImpersonation,
} from '../../utils/impersonation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatRemaining = (endsAt) => {
  if (!endsAt) return '';
  const diff = endsAt.getTime() - Date.now();
  if (diff <= 0) return 'expired';
  const minutes = Math.floor(diff / 60000);
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m left`;
  }
  return `${minutes}m left`;
};

export const ImpersonationBanner = () => {
  const [, force] = useState(0);

  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 30_000);
    const onStorage = () => force((n) => n + 1);
    window.addEventListener('storage', onStorage);
    return () => {
      clearInterval(t);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const handleExit = useCallback(async () => {
    const targetUser = JSON.parse(localStorage.getItem('user') || '{}');
    const adminToken = localStorage.getItem('impersonation_admin_token');
    if (adminToken && targetUser?.id) {
      try {
        await axios.post(
          `${API}/admin/users/${targetUser.id}/impersonate/stop`,
          {},
          { headers: { Authorization: `Bearer ${adminToken}` } }
        );
      } catch {
        // best-effort audit; don't block exit
      }
    }
    stopImpersonation();
    toast.success('Exited impersonation');
    // Send admin back to their panel
    window.location.assign('/admin');
  }, []);

  if (!isImpersonating()) return null;

  const targetEmail = getImpersonationTargetEmail();
  const endsAt = getImpersonationEndsAt();
  const remaining = formatRemaining(endsAt);

  return (
    <div
      data-testid="impersonation-banner"
      role="alert"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10500,
        background: '#DC2626',
        color: '#FFFFFF',
        padding: '8px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontFamily: "'Inter', system-ui, sans-serif",
        boxShadow: '0 2px 8px rgba(220, 38, 38, 0.4)',
      }}
    >
      <ShieldAlert size={18} />
      <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
        Impersonating <span data-testid="impersonation-target-email">{targetEmail}</span>
        {remaining && <span style={{ marginLeft: 10, fontWeight: 500, opacity: 0.85 }}>· {remaining}</span>}
      </span>
      <button
        type="button"
        onClick={handleExit}
        data-testid="impersonation-exit-button"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: '#FFFFFF',
          color: '#DC2626',
          border: 'none',
          borderRadius: 999,
          padding: '6px 14px',
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        <LogOut size={14} /> Exit
      </button>
    </div>
  );
};
